import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Tooltip, OverlayTrigger } from 'react-bootstrap';
import VitalIcons from '../../../components/icons/VitalIcons';
import BatteryCard from './BatteryCard';
import AlertBadge from './AlertBadge';
import CustomDropdownToggle from '../../../components/CustomDropdownToggle';
import { isItemActivelySilenced, getAlertColors } from '../../../utils/alertUtils';
import { formatTime, formatDateWithFullMonth, formatDateWithTime } from '../../../utils/dateUtils';
import styles from '../InpatientPortal.module.css';
import { useSelector } from 'react-redux';
import { SqiAlertIcon } from '../../../components/icons/Icons';

const VitalSummaryCard = ({
  className,
  vitals,
  batteryLevel,
  lastRecordedTimestamp,
  onPatientClick,
  patient,
  onAction
}) => {


  const dateFormat = useSelector((state) => state.userInfo.dateFormat);
  const getCardClassName = () => {
    let cardClass = styles.VitalSummaryCard;

    switch (patient.state) {
      case "alert-low":
        cardClass += ` ${styles.VitalSummaryCardAlertMedium}`;
        break;
      case "alert-medium":
        cardClass += ` ${styles.VitalSummaryCardAlertMedium}`;
        break;
      case "alert-high":
        cardClass += ` ${styles.VitalSummaryCardAlertHigh}`;
        break;
      case "inactive":
      case "empty-battery":
      default:
        cardClass += ` ${styles.VitalSummaryCardDefault}`;
        break;
    }

    return `${cardClass} ${className || ''}`;
  };

  const getDeviceId = () => {
    return patient.enrollment[0].devices[0].serialNumber;
  };

  const deviceId = getDeviceId();

  const getVitalRowClassName = (vitalState) => {
    let rowClass = styles.VitalRow;

    switch (vitalState) {
      case "alert-medium":
        rowClass += ` ${styles.VitalRowAlertMedium}`;
        break;
      case "alert-high":
        rowClass += ` ${styles.VitalRowAlertHigh}`;
        break;
      default:
        rowClass += ` ${styles.VitalRowDefault}`;
        break;
    }

    return rowClass;
  };

  const getVitalValueClassName = (vitalState) => {
    let valueClass = styles.VitalValueContainer;

    switch (vitalState) {
      case "alert-medium":
        valueClass += ` ${styles.VitalValueAlertMedium}`;
        break;
      case "alert-high":
        valueClass += ` ${styles.VitalValueAlertHigh}`;
        break;
      case "disconnected":
        valueClass += ` ${styles.VitalValueDisconnected}`;
        break;
      default:
        valueClass += ` ${styles.VitalValueDefault}`;
        break;
    }

    return valueClass;
  };

  const getBackgroundColorForVital = (vital) => {
    let backgroundColor = 'transparent';
    let color = 'inherit';
    let borderColor = 'transparent';

    if (vital.readingAccuracy === "unacceptable") {
      return { color, backgroundColor, borderColor };
    } else if (vital.background_color === "yellow") {
      backgroundColor = '#f6da67';
    } else if (vital.background_color === "red") {
      const alertColors = getAlertColors("red");
      backgroundColor = alertColors.background;
    } else if (vital.background_color === "green") {
      color = 'green';
    }

    return { color, backgroundColor, borderColor };
  };

  const formatSignalQuality = (readingAccuracy, displayValue) => {
    if (readingAccuracy === "unacceptable") {
      return <SqiAlertIcon switchColors={true} />
    } else if (readingAccuracy === "acceptable") {
      return <p className={styles.VitalValueText}><SqiAlertIcon />{displayValue}</p>;
    } else {
      return <p className={styles.VitalValueText}>{displayValue}</p>;
    }
  };

  const isDisconnectedState = patient.state === "inactive" || patient.state === "empty-battery";
  const showDashes = patient.state === "empty-battery";

  const renderVital = (iconType, vital) => {
    const actualState = vital.state;
    const isDisconnected = isDisconnectedState;
    const displayValue = showDashes ? "--" : vital.value;
    const hasCompletedDate = vital.completedDate && !showDashes;
    const vitalColors = getBackgroundColorForVital(vital);

    const vitalContent = (
      <div
        className={getVitalRowClassName(actualState)}
        style={{
          backgroundColor: vitalColors.backgroundColor,
          borderRadius: '4px',
        }}
      >
        <VitalIcons
          className={styles.VitalIconContainer}
          modalityId={vital.modalityId}
          isDisconnected={isDisconnected}
        />
        <div
          className={getVitalValueClassName(isDisconnected ? "disconnected" : actualState)}
          style={{ color: vitalColors.color }}
        >
          {formatSignalQuality(vital.readingAccuracy, displayValue)}
        </div>
      </div>
    );

    if (hasCompletedDate) {
      const renderTooltip = (props) => (
        <Tooltip id={`tooltip-${iconType}-${patient?.id || patient.fullName}`} {...props}>
          {vital.readingAccuracy === 'unacceptable' && (
            <>
              {`Unacceptable signal quality, no metrics provided.`}
              <br />
            </>
          )}
          {vital.readingAccuracy === 'acceptable' && (
            <>
              Signal quality is low, interpret with caution.
              <br />
            </>
          )}
          {formatDateWithTime(vital.completedDate, dateFormat)}
        </Tooltip>
      );

      return (
        <OverlayTrigger
          placement="top"
          delay={{ show: 50, hide: 50 }}
          overlay={renderTooltip}
        >
          {vitalContent}
        </OverlayTrigger>
      );
    }

    return vitalContent;
  };

  const getAlertBadgeStatus = () => {
    if (patient.state === "inactive") return "out-of-range";
    if (patient.state === "alert-high") return "high";
    if (patient.state === "alert-medium") return "medium";
    if (patient.state === "alert-low") return "low";
    return null;
  };


  const alertBadgeStatus = getAlertBadgeStatus();
  const hasActiveAlarm = patient.alerts.length > 0;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const cardRef = useRef(null);
  
  // Count active alerts (filter out silenced ones)
  const activeAlerts = patient.alerts.filter(alert => !isItemActivelySilenced(alert));
  const hasMultipleAlerts = activeAlerts.length > 1;

  // Check if alarm is silenced
  const isSilenced = patient?.silenceTimestamp && isItemActivelySilenced({
    alert_silenceTimestamp: patient.silenceTimestamp
  });

  const handleClick = (e) => {
    if (e.target.closest('.dropdown-menu') || e.target.closest('.dropdown')) {
      return;
    }
    e.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleDocumentMouseDown = (event) => {
      if (!cardRef.current) return;
      if (cardRef.current.contains(event.target)) return;
      setDropdownOpen(false);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [dropdownOpen]);

  const handleAction = (action) => {
    setDropdownOpen(false);
    if (onAction) {
      onAction(action, patient);
    }
  };

  return (
    <div
      className={getCardClassName()}
      onClick={handleClick}
      ref={cardRef}
      style={{
        cursor: 'pointer',
        position: 'relative'
      }}
    >
      <div className={styles.TextSection}>
        <p className={styles.FloorRoomText}>
          {`Floor ${patient.floor} | ${patient.room} | ${patient.bed}`}
        </p>
        <p className={styles.CardPatientName}>
          {patient.fullName}
        </p>
      </div>

      <div className={styles.VitalsSection}>
        {renderVital("heart-rate", vitals.heartRate)}
        {renderVital("respiratory-rate", vitals.respiratoryRate)}
        {renderVital("oxygen-saturation", vitals.oxygenSaturation)}
        {renderVital("temperature", vitals.temperature)}
      </div>

      <div className={styles.AdditionalInfoSection}>
        <div>
          <p className={styles.StatusText}>
            Status: {patient.status}
            {isSilenced && (
              <span style={{
                marginLeft: '8px',
                color: '#2A7F38',
                fontSize: '11px',
                fontStyle: 'italic'
              }}>
                (Snoozed)
              </span>
            )}
          </p>
          {lastRecordedTimestamp && (
            <p style={{
              fontSize: '10px',
              color: '#6c757d',
              marginTop: '4px',
              marginBottom: 0
            }}>
              Last recorded: {formatDateWithTime(lastRecordedTimestamp, dateFormat)}
            </p>
          )}
        </div>
        <div className={styles.DeviceInfoSection}>
          <div className={styles.DeviceIdSection}>
            <p className={styles.DeviceIdText}>
              Device: {deviceId}
            </p>
          </div>
          <BatteryCard value={batteryLevel} />
        </div>
      </div>

      {onAction && (
        <div
          style={{ position: 'absolute', right: '-8px', top: '-8px', zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            show={dropdownOpen}
            onToggle={(isOpen) => setDropdownOpen(isOpen)}
            autoClose={false}
          >
            <Dropdown.Toggle
              as={CustomDropdownToggle}
              id={`alarm-dropdown-${patient?.id || patient.fullName}`}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                position: 'relative',
                cursor: hasActiveAlarm ? 'pointer' : 'default',
                zIndex: 1000
              }}
            >
              {hasActiveAlarm ? (
                <AlertBadge status={alertBadgeStatus} />
              ) : (
                <span
                  style={{
                    width: 0,
                    height: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    display: 'block',
                    pointerEvents: 'none'
                  }}
                >
                  Menu
                </span>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="custom-bootstrap-menu"
              style={{ zIndex: 1050, position: 'absolute' }}
              popperConfig={{
                modifiers: [
                  {
                    name: 'zIndex',
                    enabled: true,
                    options: {
                      zIndex: 1050
                    }
                  }
                ]
              }}
            >
              <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('viewVitals'); }}>
                View Vitals
              </Dropdown.Item>
              {hasActiveAlarm && (
                hasMultipleAlerts ? (
                  <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('listAlarms'); }}>
                    List Alarms
                  </Dropdown.Item>
                ) : (
                  <>
                    <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('acknowledge'); }}>
                      Acknowledge Alarm
                    </Dropdown.Item>
                    <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('details'); }}>
                      Alarm Details
                    </Dropdown.Item>
                  </>
                )
              )}
              <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('updateEnrollment', patient); }}>
                Update Care Program
              </Dropdown.Item>
              <Dropdown.Item onClick={(e) => { e.stopPropagation(); handleAction('discharge', patient); }}>
                Discharge
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}
     
    </div>
  );
};

export default VitalSummaryCard;